import { useMemo } from 'react';

import { GbBreakdown, MeteringAITokens, MeteringDataUsage, SapMeteringInfo } from './apiTypes';
import {
  BreakdownRow,
  CapacityProjection,
  CapacityStatus,
  CategoryMetric,
  EnvironmentUsageRow,
  SapUsageDashboardData
} from './types';
import { formatCredits, formatNumber, ratioAgainstMax, toPercent } from './utils';

const TOKENS_PER_MILLION = 1_000_000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function gbRows({ processed, egressed, stored }: GbBreakdown): BreakdownRow[] {
  const all = [processed, egressed, stored];
  const row = (label: string, gb: number): BreakdownRow => ({
    label,
    value: `${formatNumber(gb, 0)} GB`,
    ratio: ratioAgainstMax(gb, all)
  });

  return [row('Processed', processed), row('Egressed', egressed), row('Stored', stored)];
}

function tokenRows(tokens: MeteringAITokens): BreakdownRow[] {
  const all = [tokens.newInputTokens, tokens.cacheReadTokens, tokens.cacheWriteTokens, tokens.outputTokens];
  const row = (label: string, count: number): BreakdownRow => ({
    label,
    value: `${formatNumber(count / TOKENS_PER_MILLION, 1)}M`,
    ratio: ratioAgainstMax(count, all)
  });

  return [
    row('New input', tokens.newInputTokens),
    row('Cache read', tokens.cacheReadTokens),
    row('Cache write', tokens.cacheWriteTokens),
    row('Output', tokens.outputTokens)
  ];
}

function totalGb({ processed, egressed, stored }: GbBreakdown): number {
  return processed + egressed + stored;
}

function aiCaption(tokens?: MeteringAITokens): string {
  if (!tokens) return 'Across all pipelines';
  const total = tokens.newInputTokens + tokens.cacheReadTokens + tokens.cacheWriteTokens + tokens.outputTokens;
  return `${formatNumber(total / TOKENS_PER_MILLION, 1)}M tokens across all pipelines`;
}

/** Both cycle dates are required: without them the pace figures have nothing to describe. */
function buildProjection(info: SapMeteringInfo): CapacityProjection | undefined {
  const { capacity } = info;
  if (!capacity.cycleStart || !capacity.cycleEnd) return undefined;

  const exhaustionDate = parseDate(capacity.projectedExhaustionDate);

  return {
    burnRatePerDay: capacity.burnRatePerDay,
    daysBeforeCycleEnds: capacity.daysRemainingInCycle,
    daysLeftAtCurrentPace: daysUntil(exhaustionDate),
    projectedCycleEndCredits: capacity.projectedCycleEndUsage,
    exhaustionDate
  };
}

/** The API types this as an unvalidated string, and date-fns throws on an Invalid Date. */
function parseDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** The API gives the exhaustion date, not the day count the card renders. */
function daysUntil(date?: Date): number | undefined {
  if (!date) return undefined;
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / MS_PER_DAY));
}

function determineCapacityStatus({
  consumed,
  allocated,
  projectedCycleEndCredits
}: {
  consumed: number;
  allocated: number;
  projectedCycleEndCredits?: number;
}): CapacityStatus {
  if (consumed >= allocated) return 'exhausted';
  if (projectedCycleEndCredits !== undefined && projectedCycleEndCredits > allocated) return 'runningOutFast';
  return 'onTrack';
}

function buildDataCategory(data: MeteringDataUsage): CategoryMetric {
  return {
    key: 'data',
    label: 'Data',
    credits: data.credits,
    caption: `${formatNumber(totalGb(data.gbBreakdown), 0)} GB processed, moved & stored`,
    sharePercent: data.shareOfUsage,
    breakdown: gbRows(data.gbBreakdown)
  };
}

function buildEnvironments(info: SapMeteringInfo): EnvironmentUsageRow[] {
  return info.environments.map((environment) => ({
    id: environment.executionPlaneID,
    name: environment.name || environment.controlPlaneURL || environment.executionPlaneID,
    tenantId: environment.tenantID || environment.controlPlaneURL,
    dataCredits: environment.data.credits,
    dataBreakdown: gbRows(environment.data.gbBreakdown)
  }));
}

function cycleElapsedPercentOf({ cycleStart, cycleEnd }: SapMeteringInfo['capacity']): number {
  if (!cycleStart || !cycleEnd) return 0;
  const start = new Date(cycleStart).getTime();
  const span = new Date(cycleEnd).getTime() - start;
  if (!Number.isFinite(span) || span <= 0) return 0;
  return toPercent(Date.now() - start, span);
}

export function useSapUsageDashboard(info: SapMeteringInfo): SapUsageDashboardData {
  return useMemo(() => {
    const { capacity, usageGroups } = info;
    const rates = info.rates;
    const projection = buildProjection(info);
    const environments = buildEnvironments(info);
    const environmentCredits = environments.reduce((sum, env) => sum + env.dataCredits, 0);
    const infrastructureRates = rates?.infrastructure;

    return {
      creditsConsumed: capacity.consumed,
      totalCreditsAllocated: capacity.allocated,
      usedPercent: toPercent(capacity.consumed, capacity.allocated),
      cycleElapsedPercent: cycleElapsedPercentOf(capacity),
      status: determineCapacityStatus({
        consumed: capacity.consumed,
        allocated: capacity.allocated,
        projectedCycleEndCredits: projection?.projectedCycleEndCredits
      }),
      projection,
      categories: [
        buildDataCategory(usageGroups.data),
        {
          key: 'ai',
          label: 'AI',
          credits: usageGroups.ai.credits,
          caption: aiCaption(usageGroups.ai.tokens),
          sharePercent: usageGroups.ai.shareOfUsage,
          breakdown: usageGroups.ai.tokens ? tokenRows(usageGroups.ai.tokens) : []
        },
        {
          key: 'infrastructure',
          label: 'Infrastructure',
          credits: usageGroups.infrastructure.credits,
          caption: 'Users, control plane & data planes',
          sharePercent: usageGroups.infrastructure.shareOfUsage,
          breakdown: []
        }
      ],
      environments,
      environmentsSummary: {
        totalCredits: capacity.consumed,
        environmentCredits,
        // Whatever the per-environment rows don't account for is billed at account level.
        accountLevelCredits: capacity.consumed - environmentCredits
      },
      seats: {
        totalSeats: info.totalUsers,
        activatedSeats: info.activatedUsers,
        includedUsers: info.includedUsers,
        additionalUsers: info.additionalUsers,
        creditsPerAdditionalUser: infrastructureRates?.perAdditionalUser
      },
      dataRates: rates?.data
        ? [
            { value: formatNumber(rates.data.perGbProcessed, 4), label: 'gb Processed' },
            { value: formatNumber(rates.data.perGbEgressed, 4), label: 'gb Egressed' },
            { value: formatNumber(rates.data.perGbStoredPerMonth, 4), label: 'gb Stored / Month' }
          ]
        : [],
      // The AI rate card is deliberately not exposed to end users, so `rates.ai` arrives omitted.
      aiRateNote: 'Based Directly on Consumption',
      infrastructureRates: infrastructureRates
        ? [
            { value: formatCredits(infrastructureRates.perAdditionalUser), label: 'cu / additional user' },
            {
              value: formatCredits(infrastructureRates.perDedicatedControlPlane),
              label: 'cu / dedicated control plane'
            },
            {
              value: formatCredits(infrastructureRates.perAdditionalExecutionPlane),
              label: `cu / additional data plane (first ${infrastructureRates.includedExecutionPlanes} included)`
            }
          ]
        : []
    };
  }, [info]);
}
